import {
  Autocomplete,
  Button,
  CircularProgress,
  ClickAwayListener,
  IconButton,
  Link,
  Stack,
  TextField,
  Tooltip,
  Typography
} from "@mui/material";
import React from "react";
import axios from "axios";
import { MRT_ColumnDef } from "material-react-table";
import { useEffect, useState } from "react";
import Iconify from "../../../../minimals/components/iconify";
import { DashboardNote } from "../../../checks/types";
import ChecklistIcon from "../../../orders/ChecklistIcon";
import StatusCountsFilter from "../../../orders/CustomRecordedDocumentsFilter";
import DownloadFileButton from "../../../recordables/DownloadFileButton";
import { downloadPolicyDocuments } from "../policiestosenddashbord/downloadPolicyDocuments";
import ReviseIcon from "./ReviseIcon";
import { Policy, RecordableStatusCounts } from "./types";
import { AutocompleteFilter, createAutocompleteFilterFn, createCellRenderer } from "../../generateAutocompleteColumnAndFilter";

const policyStatusOptions = [
  { value: 1, label: "Not Ready" },
  { value: 2, label: "Ready to Send" }
];
export const getColumns = (
  handleOpenModal: (selectedPolicy: Policy) => void,
  setSelectedPolicy: (selectedPolicy: Policy) => void,
  handleOpenNotesIssuesModal: (selectedPolicy: Policy) => void,
  isUsersShowEditPolicyIssues: boolean,
  handleOpenRevisedPolicy: (selectedPolicy: Policy) => void,
  handleOpenEditModal: (selectedPolicy: Policy) => void,
  setPolicies: React.Dispatch<React.SetStateAction<Policy[]>>
): MRT_ColumnDef<Policy>[] => {
  const momentumUrl = import.meta.env.VITE_MOMENTUM_URL;
  return [
    {
      id: "notes",
      header: "",
      enableColumnFilter: false,
      enableColumnActions: false,
      enableColumnDragging: false,
      enableColumnFilterModes: false,
      size: 50,
      enableHiding: false
    },
    {
      header: "Department",
      id: "dept",
      accessorFn: (row) => row.department || "",
      filterFn: createAutocompleteFilterFn<Policy>("department"),
      Filter: ({ table, column }) => (
        <AutocompleteFilter table={table} column={column} accessorPath="department" placeholder="Department" />
      ),
      Cell: createCellRenderer<Policy>("department"),
      size: 120,
      muiFilterTextFieldProps: {
        variant: "outlined",
        size: "small"
      }
    },
    {
      header: "Policy Number",
      accessorKey: "policyNumber",
      id: "policyNumber",
      size: 50,
      muiFilterTextFieldProps: {
        size: "small"
      },
      Cell: ({ renderedCellValue, row }) => <Typography variant="body2">{row.original.policyNumber}</Typography>
    },
    {
      header: "Order Number",
      accessorKey: "orderNumber",
      id: "orderNumber",
      size: 50,
      muiFilterTextFieldProps: {
        size: "small"
      },
      Cell: ({ renderedCellValue, row }) => (
        <Stack direction="row" alignItems="center" spacing={1}>
        <IconButton href={`/task-management/closingchecklist?order=${row.original.orderNumber}`} target="_blank">
          <ChecklistIcon />
        </IconButton>
        <Link href={`${momentumUrl}new-home/${row.original.orderNumber}`} target="_blank">
          <Typography variant="body2">{row.original.orderNumber}</Typography>
        </Link>
        </Stack>
      )
    },
    {
      header: "Policy Type",
      accessorKey: "type",
      id: "type",
      filterVariant: "select",
      size: 80,
      filterSelectOptions: [
        { label: "Owners", value: 1 },
        { label: "Loan", value: 2 }
      ],
      muiFilterTextFieldProps: {
        variant: "outlined",
        size: "small",
        select: true
      },
      Cell: ({ renderedCellValue, row }) => (
        <Stack direction={"row"} alignItems={"center"}>
          <Stack width={80} direction={"row"} alignItems={"center"}>
            {row.original.status !== 0 ? (
              //<DownloadFileButton fileId={row.original.documentId} />
              <IconButton
                onClick={() => downloadPolicyDocuments(row.original)}
                disabled={row.original.associatedDocuments?.length === 0 && !row.original.documentId}
              >
                <Iconify icon="eva:download-fill" />
              </IconButton>
            ) : (
              <div style={{ width: 36 }} />
            )}
            {row.original.status !== 0 && row.original.status !== 3 && (
              <IconButton aria-label="edit" onClick={() => handleOpenEditModal(row.original)}>
                <Iconify icon={"material-symbols:edit-outline"} sx={{ width: 24, height: 24 }} />
              </IconButton>
            )}
            {row.original.status === 3 && (
              <IconButton>
                <ReviseIcon onClick={() => handleOpenRevisedPolicy(row.original)} sx={{ cursor: "pointer" }} />
              </IconButton>
            )}
          </Stack>
          <Typography variant="body2" width={100}>
            {+row.original.type === 2 ? "Loan " : "Owners "}
            {row.original.position && row.original.position}
            {row.original.revision > 0 ? ` Revision ${row.original.revision}` : ""}
          </Typography>
        </Stack>
      )
    },
    {
      header: "Due",
      accessorKey: "due",
      id: "due",
      size: 50,
      accessorFn: (row) => {
        const taskReceivedDate = row.taskReceivedDate;
        if (!taskReceivedDate) return null;
        const start = new Date(taskReceivedDate);
        if (isNaN(start.getTime())) return null;
        const dueDate = new Date(start);
        dueDate.setDate(dueDate.getDate() + 29);
        const today = new Date();
        const dateOnly = (d: any) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
        return Math.round((dateOnly(dueDate).getTime() - dateOnly(today).getTime()) / (24 * 60 * 60 * 1000));
      },
      sortingFn: "basic",
      sortDescFirst: false
    },
    {
      header: "Assigned to",
      accessorKey: "assignedToUser",
      id: "assignedToUser",
      size: 50,
      accessorFn: (row) => row.assignedToUser?.fullName || row.assignedToName || "",
      Cell: ({ renderedCellValue, row }) => (
        <Typography variant="body2">{row.original?.assignedToUser?.fullName || row.original?.assignedToName}</Typography>
      ),
      muiFilterTextFieldProps: {
        variant: "outlined",
        size: "small"
      }
    },
    {
      header: "Issues",
      accessorKey: "issue",
      filterVariant: "select",
      filterSelectOptions: [
        { label: "Unresolved", value: 1 },
        { label: "Resolved", value: 2 },
        { label: "Written Off", value: 3 }
      ],
      muiFilterTextFieldProps: {
        variant: "outlined",
        size: "small",
        select: true
      },
      filterFn: (row, columnId, filterValue) => {
        return filterValue === "" || row.getValue(columnId) == filterValue;
      },
      Cell: ({ renderedCellValue, row }) => (
        <Stack direction={"row"} alignItems={"center"}>
          <Typography variant="body2" width={80}>
            {row.original.issue && +row.original.issue === 2
              ? "Resolved"
              : row.original.issue && +row.original.issue === 3
              ? "Written Off"
              : row.original.issue && +row.original.issue === 1
              ? "Unresolved"
              : ""}
          </Typography>
          {isUsersShowEditPolicyIssues && (
            <IconButton onClick={() => handleOpenNotesIssuesModal(row.original)} aria-label="edit">
              <Iconify icon={"material-symbols:edit-outline"} sx={{ width: 24, height: 24 }} />
            </IconButton>
          )}
        </Stack>
      )
    },
    {
      header: "Status",
      filterVariant: "select",
      accessorKey: "status",
      filterSelectOptions: [
        { label: "Not Ready", value: 1 },
        { label: "Ready to Send", value: 2 },
        { label: "Sent", value: 3 }
      ],
      id: "status",
      Cell: ({ renderedCellValue, row }) => {
        const [loading, setLoading] = useState(false);
        const [status, setStatus] = useState(row.original.status);
        const onChangeAction = async (newStatus: any) => {
          setLoading(true);
          const updatedPolicy = { ...row.original, status: newStatus?.value };
          await axios.post(
            updatedPolicy.status === 2 //Ready to Send
              ? "/api/outstandingChecks/UpdatePolicyCloser"
              : "/api/outstandingChecks/UpdatePolicy",
            updatedPolicy
          );
          setStatus(newStatus?.value);
          setPolicies((prev) =>
            prev.map((p) => (p.uniqueIdentifier === row.original.uniqueIdentifier ? updatedPolicy : p))
          );
          setLoading(false);
        };
        return (
          <>
            {isUsersShowEditPolicyIssues && (
              <div>
                {row.original.status === 0 ? (
                  <Button
                    onClick={() => {
                      handleOpenModal(row.original);
                      setSelectedPolicy(row.original);
                    }}
                  >
                    Upload Policy
                  </Button>
                ) : row.original?.status && +row.original?.status === 3 ? (
                  <Stack direction={"row"} alignItems={"center"}>
                    <DownloadFileButton fileId={row.original.transmittedDocumentId} />
                    <Typography>Sent</Typography>
                  </Stack>
                ) : (
                  <Autocomplete
                    options={policyStatusOptions || []}
                    getOptionLabel={(option) => option.label || ""}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Status"
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {loading ? <CircularProgress color="inherit" size={20} /> : null}
                              {params.InputProps.endAdornment}
                            </>
                          )
                        }}
                      />
                    )}
                    loading={loading}
                    onChange={(e, value) => onChangeAction(value)}
                    value={policyStatusOptions.find((option) => option.value === status) || null}
                    sx={{ width: 200 }}
                  />
                )}
              </div>
            )}
          </>
        );
      },
      muiFilterTextFieldProps: {
        variant: "outlined",
        size: "small",
        select: true
      }
    },
    {
      header: "Search Update",
      accessorKey: "searchUpdate",
      id: "searchUpdate",
      size: 50,
      Cell: ({ renderedCellValue, row }) => {
        const orderNumber = row.original.orderNumber;
        const [searchStatus, setSearchStatus] = useState("Not Ordered");
        const [tasksNotes, setTasksNotes] = useState<DashboardNote[]>([]);
        const [open, setOpen] = useState(false);
        const [isLoading, setIsLoading] = useState(false);
        const handleTooltipClose = () => {
          setOpen(false);
        };
        const handleTooltipOpen = () => {
          setOpen(true);
        };
        useEffect(() => {
          if (row.original.checklistTaskStatus === "Completed") {
            setSearchStatus("Reviewed");
          } else if (row.original.requestedTaskStatus === "Requested") {
            setSearchStatus("Ordered");
          } else if (row.original.requestedTaskStatus === "Received") {
            setSearchStatus("Received");
          } else {
            setSearchStatus("Not Ordered");
          }
          setTasksNotes(row.original.taskNotes);
        }, [orderNumber]);
        return (
          <Typography variant="body2">
            {isLoading ? <CircularProgress size={16} /> : searchStatus}
            {tasksNotes?.length > 0 && (
              <ClickAwayListener onClickAway={handleTooltipClose}>
                <Tooltip
                  title={
                    <div>
                      {tasksNotes?.map((tn, index) => (
                        <div key={index}>{tn?.text}</div>
                      ))}
                    </div>
                  }
                  onClose={handleTooltipClose}
                  open={open}
                  disableFocusListener
                  disableHoverListener
                  arrow
                >
                  <IconButton aria-label="edit" onClick={handleTooltipOpen}>
                    <Iconify icon={"grommet-icons:note"} sx={{ width: 14, height: 14 }} />
                  </IconButton>
                </Tooltip>
              </ClickAwayListener>
            )}
          </Typography>
        );
      }
    },
    {
      id: "statusCounts",
      header: "Recorded Documents",
      accessorKey: "statusCounts",
      enableColumnFilter: true,
      filterVariant: "multi-select",
      Filter: ({ column }) => <StatusCountsFilter column={column} placeholder="Filter by Document Status" />,
      filterSelectOptions: [
        { text: "None Sent/Recorded", value: "None Sent/Recorded" },
        { text: "Rejected", value: "Rejected" },
        { text: "Some Sent/Recorded", value: "Some Sent/Recorded" },
        { text: "All Recorded", value: "All Recorded" },
        { text: "No Documents", value: "No Documents" },
        { text: "N/A", value: "N/A" }
      ],
      filterFn: (row, columnId, filterValue) => {
        const { notSent, sent, recorded, rejected } = row?.original?.statusCounts || {};
        const totalDocuments = notSent + sent + recorded + rejected;

        if (!filterValue.length) return true;

        return filterValue.some((value: any) => {
          switch (value) {
            case "N/A":
              return row?.original?.isRecordablesNotApplicable;
            case "Rejected":
              return rejected > 0;
            case "All Recorded":
              return recorded > 0 && recorded === totalDocuments;
            case "Some Sent/Recorded": //If some are recorded or some or all are sent
              return (recorded > 0 || sent > 0) && recorded < totalDocuments;
            case "None Sent/Recorded":
              return totalDocuments > 0 && recorded === 0 && sent === 0 && rejected == 0;
            case "No Documents":
              return totalDocuments === 0 && !row?.original?.isRecordablesNotApplicable;
            default:
              return true;
          }
        });
      },
      sortingFn: (rowA, rowB) => {
        const getStatusOrder = (statusCounts: RecordableStatusCounts, isRecordablesNotApplicable: boolean) => {
          const { notSent, sent, recorded, rejected } = statusCounts;
          const totalDocuments = notSent + sent + recorded + rejected;
          if (totalDocuments === 0 && isRecordablesNotApplicable) return 4;
          if (notSent > 0 && recorded === 0 && sent === 0) return 0;
          if (rejected > 0) return 1; // Rejected
          if (recorded > 0 && recorded < totalDocuments) return 2;
          if (recorded > 0 && recorded === totalDocuments) return 3;
          return 5;
        };

        const statusOrderA = getStatusOrder(
          rowA.original.statusCounts,
          Boolean(rowA?.original?.isRecordablesNotApplicable)
        );
        const statusOrderB = getStatusOrder(
          rowB.original.statusCounts,
          Boolean(rowB?.original?.isRecordablesNotApplicable)
        );
        return statusOrderA - statusOrderB;
      },
      Cell: ({ row }) => {
        // const settlementDate = row?.original?.settlementDate;
        // const today = new Date();
        // today.setHours(0, 0, 0, 0);
        // const isSettlementDateAfterToday = settlementDate && new Date(settlementDate) < today;

        const { notSent, sent, recorded, rejected } = row?.original?.statusCounts;

        if ([notSent, sent, recorded, rejected].every((e) => e === 0) && row?.original?.isRecordablesNotApplicable) {
          return <Typography variant="subtitle2">N/A</Typography>;
        }

        return (
          <>
            <Link
              href={`/recordables/?order=${row.original.orderNumber}`}
              target="_blank"
              sx={{
                textDecoration: "none",
                color: "inherit",
                "&:hover": {
                  textDecoration: "none",
                  cursor: "pointer"
                }
              }}
            >
              {(notSent > 0 || rejected > 0) && (
                <>
                  <Typography variant="subtitle2">
                    {rejected > 0 && (
                      <span style={{ color: "#EF5350", marginRight: rejected > 0 && notSent > 0 ? "8px" : "0" }}>
                        {rejected} Rejected
                      </span>
                    )}
                    {notSent > 0 && <span>{notSent} Not Sent</span>}
                  </Typography>
                </>
              )}
              {(recorded > 0 || sent > 0) && (
                <>
                  <Typography variant="subtitle2">
                    {recorded > 0 && (
                      <span style={{ color: "#54D62C", marginRight: recorded > 0 && sent > 0 ? "8px" : "0" }}>
                        {recorded} Recorded
                      </span>
                    )}
                    {sent > 0 && <span style={{ color: "#FFC107" }}>{sent} Sent</span>}
                  </Typography>
                </>
              )}
            </Link>
          </>
        );
      }
    }
  ];
};
